/** Global keyboard-shortcuts help, opened with `?`. */
import { useEffect, useState } from 'react';
import { IconKeyboard } from '@tabler/icons-react';
import { Modal } from '../ui/Modal.tsx';
import { SHORTCUTS } from '../../lib/shortcuts.ts';
import { useThemeStore } from '../../state/themeStore.ts';

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable;
}

export function ShortcutsDialog() {
  const [open, setOpen] = useState(false);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore shortcuts while typing in a field.
      if (isTypingTarget(e.target)) return;
      if (e.key === '?') {
        setOpen((v) => !v);
      } else if (e.key === 't' || e.key === 'T') {
        toggleTheme();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleTheme]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden h-9 w-9 place-items-center rounded-xl border border-line bg-surface text-fg-2 shadow-sm transition duration-200 ease-standard hover:bg-grid sm:grid"
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts (?)"
      >
        <IconKeyboard className="h-5 w-5" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Keyboard shortcuts">
        <ul className="space-y-2">
          {SHORTCUTS.map((s) => (
            <li key={s.description} className="flex items-center justify-between gap-4">
              <span className="text-sm text-fg-2">{s.description}</span>
              <span className="flex gap-1">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="rounded-md border border-line bg-surface px-2 py-0.5 text-xs font-medium text-fg shadow-sm"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
